// Shared weekly-availability editor. Used by BOTH the owner-facing Agenda
// (agenda/Agenda.tsx) and the operator console (admin/TenantDetail.tsx), so a
// doctor's hours are edited the same way whoever is driving. The two sides
// differ only in their data seam (which URL prefix); everything visual lives
// here.

import { IconButton, ToggleSwitch } from '../../design-system/index.js'

// Editor day (0=Mon..6=Sun) <-> API weekday (0=Sun..6=Sat).
export const dsToApi = (day: number): number => (day + 1) % 7

// Spanish weekday labels, Monday-first (matches the editor `day` index).
export const WEEK_DAYS = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo']

// One weekday in the editor: closed, or open with a LIST of time-blocks. The DB
// (provider_schedules) allows several rows per weekday, e.g. a split shift with
// a morning block + an afternoon block, so each day holds an array, not a pair.
export interface TimeBlock {
  from: string // 'HH:MM'
  to: string // 'HH:MM'
}
export interface DayBlocks {
  day: number // 0=Mon..6=Sun
  open: boolean
  blocks: TimeBlock[]
}

// Minimal shape both data seams already return (owner ScheduleBlock / operator
// ProviderScheduleBlock both satisfy it).
export interface ScheduleRow {
  weekday: number // 0=Sun..6=Sat
  startTime: string
  endTime: string
}

// Group every schedule row by weekday (do NOT take only the first) so split
// shifts survive a load -> save round-trip.
export function buildWeek(schedules: ScheduleRow[]): DayBlocks[] {
  return Array.from({ length: 7 }, (_, day): DayBlocks => {
    const blocks = schedules
      .filter((s) => s.weekday === dsToApi(day))
      .sort((a, b) => a.startTime.localeCompare(b.startTime))
      .map((s): TimeBlock => ({ from: s.startTime, to: s.endTime }))
    return blocks.length > 0 ? { day, open: true, blocks } : { day, open: false, blocks: [] }
  })
}

// Serialize an editor week back to API rows: one row per weekday+from+to, only
// valid blocks of open days, stamping the shared per-week consultation length
// onto each row.
export function serializeWeek(
  week: DayBlocks[],
  slotMinutes: number,
): { weekday: number; startTime: string; endTime: string; slotMinutes: number }[] {
  return week.flatMap((d) =>
    d.open
      ? d.blocks
          .filter((b) => b.from && b.to && b.from < b.to)
          .map((b) => ({
            weekday: dsToApi(d.day),
            startTime: b.from,
            endTime: b.to,
            slotMinutes,
          }))
      : [],
  )
}

/**
 * ISO timestamp → es-MX "medium date, short time", pinned to
 * America/Mexico_City so prod (UTC) renders the clinic's local time.
 */
export function fmtDateTime(iso: string): string {
  return new Date(iso).toLocaleString('es-MX', {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: 'America/Mexico_City',
  })
}

function XIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.2}
      strokeLinecap="round"
      aria-hidden
    >
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  )
}

// Weekly availability editor that supports MULTIPLE time-blocks per weekday (a
// split shift = morning block + afternoon block). Each open day lists its blocks
// with an "Agregar bloque" (+) and a per-block remove (x).
export function MultiBlockWeekEditor({
  week,
  onChange,
}: {
  week: DayBlocks[]
  onChange: (week: DayBlocks[]) => void
}): JSX.Element {
  function updateDay(index: number, patch: Partial<DayBlocks>): void {
    onChange(week.map((d, i) => (i === index ? { ...d, ...patch } : d)))
  }
  function updateBlock(dayIndex: number, blockIndex: number, patch: Partial<TimeBlock>): void {
    const day = week[dayIndex]
    if (!day) return
    updateDay(dayIndex, {
      blocks: day.blocks.map((b, i) => (i === blockIndex ? { ...b, ...patch } : b)),
    })
  }
  function addBlock(dayIndex: number): void {
    const day = week[dayIndex]
    if (!day) return
    const last = day.blocks[day.blocks.length - 1]
    const next: TimeBlock = last ? { from: last.to, to: last.to } : { from: '09:00', to: '14:00' }
    updateDay(dayIndex, { open: true, blocks: [...day.blocks, next] })
  }
  function removeBlock(dayIndex: number, blockIndex: number): void {
    const day = week[dayIndex]
    if (!day) return
    const remaining = day.blocks.filter((_, i) => i !== blockIndex)
    updateDay(dayIndex, { blocks: remaining, open: remaining.length > 0 })
  }
  function toggleDay(dayIndex: number, open: boolean): void {
    const day = week[dayIndex]
    if (!day) return
    if (open) {
      updateDay(dayIndex, {
        open: true,
        blocks: day.blocks.length > 0 ? day.blocks : [{ from: '09:00', to: '14:00' }],
      })
    } else {
      updateDay(dayIndex, { open: false, blocks: [] })
    }
  }

  return (
    <div className="flex flex-col">
      {week.map((d, i) => {
        const dayName = WEEK_DAYS[d.day] ?? WEEK_DAYS[i] ?? ''
        return (
          <div key={i} className="flex flex-col gap-2 border-b border-line py-2.5 last:border-b-0">
            <div className="flex items-center gap-3">
              <span className="w-24 flex-none text-[12.5px] font-semibold text-text">
                {dayName}
              </span>
              {!d.open && <span className="text-[12px] font-medium text-text-3">Cerrado</span>}
              <ToggleSwitch
                checked={d.open}
                onChange={(open) => toggleDay(i, open)}
                label={`Abrir ${dayName}`}
                hideLabel
                size="sm"
                className="ml-auto"
              />
            </div>
            {d.open && (
              <div className="ml-24 flex flex-col gap-1.5 pl-3">
                {d.blocks.map((b, bi) => {
                  const invalid = b.from && b.to ? b.to <= b.from : false
                  return (
                    <div key={bi} className="flex flex-col gap-0.5">
                      <div className="flex items-center gap-2">
                        <input
                          type="time"
                          value={b.from}
                          aria-label={`${dayName} · bloque ${bi + 1} · desde`}
                          onChange={(e) => updateBlock(i, bi, { from: e.target.value })}
                          className="rounded-lg border border-input-line bg-surface px-2 py-1 font-mono text-[12px] text-text outline-none focus-visible:border-brand-blue"
                        />
                        <span aria-hidden className="text-text-3">
                          –
                        </span>
                        <input
                          type="time"
                          value={b.to}
                          aria-label={`${dayName} · bloque ${bi + 1} · hasta`}
                          onChange={(e) => updateBlock(i, bi, { to: e.target.value })}
                          className="rounded-lg border border-input-line bg-surface px-2 py-1 font-mono text-[12px] text-text outline-none focus-visible:border-brand-blue"
                        />
                        <IconButton
                          size="sm"
                          variant="ghost"
                          icon={<XIcon />}
                          aria-label={`Quitar bloque ${bi + 1} de ${dayName}`}
                          onClick={() => removeBlock(i, bi)}
                        />
                      </div>
                      {invalid && (
                        <span role="alert" className="text-[11px] text-sens">
                          La hora de fin debe ser posterior a la de inicio.
                        </span>
                      )}
                    </div>
                  )
                })}
                <div>
                  <button
                    type="button"
                    onClick={() => addBlock(i)}
                    className="rounded-lg border border-input-line bg-surface px-3 py-1.5 text-[12px] font-semibold text-text-2 transition-colors hover:border-brand-blue hover:text-link focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-blue"
                  >
                    Agregar bloque
                  </button>
                </div>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
